import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Form, Button, Alert, Tab, Tabs } from 'react-bootstrap';
import { FaSave, FaEnvelope, FaCreditCard, FaShieldAlt, FaDatabase, FaGlobe } from 'react-icons/fa';
import api from '../../services/api';
import { toast } from 'react-toastify';

const SuperAdminSettings = () => {
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [settings, setSettings] = useState({
        // Email settings
        email_host: '',
        email_port: 587,
        email_use_tls: true,
        email_use_ssl: false,
        email_host_user: '',
        email_host_password: '',
        default_from_email: '',

        // Payment settings
        stripe_publishable_key: '',
        stripe_secret_key: '',
        paypal_client_id: '',
        paypal_client_secret: '',
        visa_fee: 299,
        currency: 'EUR',

        // Security settings
        session_timeout: 3600,
        max_login_attempts: 5,
        password_min_length: 8,
        two_factor_required: false,

        // System settings
        maintenance_mode: false,
        debug_mode: false,
        log_level: 'INFO',
        backup_frequency: 'daily',

        // General settings
        site_name: 'EVisa Platform',
        site_description: '',
        contact_email: '',
        support_phone: '',
    });

    useEffect(() => {
        loadSettings();
    }, []);

    const loadSettings = async () => {
        try {
            const response = await api.get('/superadmin/settings/');
            setSettings({ ...settings, ...response.data });
        } catch (error) {
            console.error('Error loading settings:', error);
            toast.error('Erreur lors du chargement des paramètres');
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setSettings({
            ...settings,
            [name]: type === 'checkbox' ? checked : value,
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            await api.post('/superadmin/settings/', settings);
            toast.success('✅ Paramètres sauvegardés avec succès');
        } catch (error) {
            console.error('Error saving settings:', error);
            toast.error('❌ Erreur lors de la sauvegarde');
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <Container className="py-4">
                <div className="text-center py-5">
                    <div className="spinner-border text-primary"></div>
                    <p className="mt-3">Chargement des paramètres...</p>
                </div>
            </Container>
        );
    }

    return (
        <Container className="py-4">
            <Row className="mb-4">
                <Col>
                    <h2>
                        <FaShieldAlt className="me-3" />
                        Paramètres Système
                    </h2>
                    <p className="text-muted">Configuration générale de la plateforme</p>
                </Col>
            </Row>

            <Form onSubmit={handleSubmit}>
                <Tabs defaultActiveKey="general" className="mb-4">
                    {/* Onglet Général */}
                    <Tab eventKey="general" title={<><FaGlobe className="me-2" />Général</>}>
                        <Card>
                            <Card.Header>
                                <h5>Informations générales</h5>
                            </Card.Header>
                            <Card.Body>
                                <Row>
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Nom du site *</Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="site_name"
                                                value={settings.site_name}
                                                onChange={handleChange}
                                                required
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Email de contact *</Form.Label>
                                            <Form.Control
                                                type="email"
                                                name="contact_email"
                                                value={settings.contact_email}
                                                onChange={handleChange}
                                                required
                                            />
                                        </Form.Group>
                                    </Col>
                                </Row>

                                <Form.Group className="mb-3">
                                    <Form.Label>Description du site</Form.Label>
                                    <Form.Control
                                        as="textarea"
                                        rows={3}
                                        name="site_description"
                                        value={settings.site_description}
                                        onChange={handleChange}
                                    />
                                </Form.Group>

                                <Form.Group className="mb-3">
                                    <Form.Label>Téléphone support</Form.Label>
                                    <Form.Control
                                        type="tel"
                                        name="support_phone"
                                        value={settings.support_phone}
                                        onChange={handleChange}
                                    />
                                </Form.Group>

                                <Form.Group className="mb-3">
                                    <Form.Label>Devise *</Form.Label>
                                    <Form.Select
                                        name="currency"
                                        value={settings.currency}
                                        onChange={handleChange}
                                    >
                                        <option value="EUR">Euro (€)</option>
                                        <option value="USD">Dollar ($)</option>
                                        <option value="GBP">Livre sterling (£)</option>
                                    </Form.Select>
                                </Form.Group>
                            </Card.Body>
                        </Card>
                    </Tab>

                    {/* Onglet Email */}
                    <Tab eventKey="email" title={<><FaEnvelope className="me-2" />Email</>}>
                        <Card>
                            <Card.Header>
                                <h5>Configuration SMTP</h5>
                            </Card.Header>
                            <Card.Body>
                                <Alert variant="info">
                                    <strong>Configuration requise :</strong> Ces paramètres sont utilisés pour l'envoi d'emails (vérification, notifications, etc.)
                                </Alert>

                                <Row>
                                    <Col md={8}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Serveur SMTP *</Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="email_host"
                                                value={settings.email_host}
                                                onChange={handleChange}
                                                placeholder="smtp.gmail.com"
                                                required
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col md={4}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Port *</Form.Label>
                                            <Form.Control
                                                type="number"
                                                name="email_port"
                                                value={settings.email_port}
                                                onChange={handleChange}
                                                required
                                            />
                                        </Form.Group>
                                    </Col>
                                </Row>

                                <Row>
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Utilisateur *</Form.Label>
                                            <Form.Control
                                                type="email"
                                                name="email_host_user"
                                                value={settings.email_host_user}
                                                onChange={handleChange}
                                                required
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Mot de passe *</Form.Label>
                                            <Form.Control
                                                type="password"
                                                name="email_host_password"
                                                value={settings.email_host_password}
                                                onChange={handleChange}
                                                required
                                            />
                                        </Form.Group>
                                    </Col>
                                </Row>

                                <Form.Group className="mb-3">
                                    <Form.Label>Email expéditeur par défaut *</Form.Label>
                                    <Form.Control
                                        type="email"
                                        name="default_from_email"
                                        value={settings.default_from_email}
                                        onChange={handleChange}
                                        required
                                    />
                                </Form.Group>

                                <Row>
                                    <Col md={6}>
                                        <Form.Check
                                            type="checkbox"
                                            label="Utiliser TLS"
                                            name="email_use_tls"
                                            checked={settings.email_use_tls}
                                            onChange={handleChange}
                                        />
                                    </Col>
                                    <Col md={6}>
                                        <Form.Check
                                            type="checkbox"
                                            label="Utiliser SSL"
                                            name="email_use_ssl"
                                            checked={settings.email_use_ssl}
                                            onChange={handleChange}
                                        />
                                    </Col>
                                </Row>
                            </Card.Body>
                        </Card>
                    </Tab>

                    {/* Onglet Paiement */}
                    <Tab eventKey="payment" title={<><FaCreditCard className="me-2" />Paiement</>}>
                        <Card>
                            <Card.Header>
                                <h5>Configuration des paiements</h5>
                            </Card.Header>
                            <Card.Body>
                                <Alert variant="warning">
                                    <strong>Sécurité :</strong> Les clés API sont sensibles. Assurez-vous qu'elles sont stockées de manière sécurisée.
                                </Alert>

                                <Form.Group className="mb-3">
                                    <Form.Label>Frais de visa (€) *</Form.Label>
                                    <Form.Control
                                        type="number"
                                        name="visa_fee"
                                        value={settings.visa_fee}
                                        onChange={handleChange}
                                        min="0"
                                        step="0.01"
                                        required
                                    />
                                </Form.Group>

                                <h6 className="mt-4 mb-3">Stripe</h6>
                                <Form.Group className="mb-3">
                                    <Form.Label>Clé publique Stripe</Form.Label>
                                    <Form.Control
                                        type="password"
                                        name="stripe_publishable_key"
                                        value={settings.stripe_publishable_key}
                                        onChange={handleChange}
                                        placeholder="pk_test_..."
                                    />
                                </Form.Group>

                                <Form.Group className="mb-3">
                                    <Form.Label>Clé secrète Stripe</Form.Label>
                                    <Form.Control
                                        type="password"
                                        name="stripe_secret_key"
                                        value={settings.stripe_secret_key}
                                        onChange={handleChange}
                                        placeholder="sk_test_..."
                                    />
                                </Form.Group>

                                <h6 className="mt-4 mb-3">PayPal</h6>
                                <Form.Group className="mb-3">
                                    <Form.Label>Client ID PayPal</Form.Label>
                                    <Form.Control
                                        type="password"
                                        name="paypal_client_id"
                                        value={settings.paypal_client_id}
                                        onChange={handleChange}
                                        placeholder="AZ..."
                                    />
                                </Form.Group>

                                <Form.Group className="mb-3">
                                    <Form.Label>Client Secret PayPal</Form.Label>
                                    <Form.Control
                                        type="password"
                                        name="paypal_client_secret"
                                        value={settings.paypal_client_secret}
                                        onChange={handleChange}
                                        placeholder="EP..."
                                    />
                                </Form.Group>
                            </Card.Body>
                        </Card>
                    </Tab>

                    {/* Onglet Sécurité */}
                    <Tab eventKey="security" title={<><FaShieldAlt className="me-2" />Sécurité</>}>
                        <Card>
                            <Card.Header>
                                <h5>Paramètres de sécurité</h5>
                            </Card.Header>
                            <Card.Body>
                                <Row>
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Timeout de session (secondes) *</Form.Label>
                                            <Form.Control
                                                type="number"
                                                name="session_timeout"
                                                value={settings.session_timeout}
                                                onChange={handleChange}
                                                min="300"
                                                required
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Tentatives de connexion max *</Form.Label>
                                            <Form.Control
                                                type="number"
                                                name="max_login_attempts"
                                                value={settings.max_login_attempts}
                                                onChange={handleChange}
                                                min="1"
                                                required
                                            />
                                        </Form.Group>
                                    </Col>
                                </Row>

                                <Form.Group className="mb-3">
                                    <Form.Label>Longueur minimale du mot de passe *</Form.Label>
                                    <Form.Control
                                        type="number"
                                        name="password_min_length"
                                        value={settings.password_min_length}
                                        onChange={handleChange}
                                        min="6"
                                        required
                                    />
                                </Form.Group>

                                <Form.Check
                                    type="checkbox"
                                    label="Authentification à deux facteurs requise"
                                    name="two_factor_required"
                                    checked={settings.two_factor_required}
                                    onChange={handleChange}
                                />
                            </Card.Body>
                        </Card>
                    </Tab>

                    {/* Onglet Système */}
                    <Tab eventKey="system" title={<><FaDatabase className="me-2" />Système</>}>
                        <Card>
                            <Card.Header>
                                <h5>Paramètres système</h5>
                            </Card.Header>
                            <Card.Body>
                                <Row>
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Niveau de logs *</Form.Label>
                                            <Form.Select
                                                name="log_level"
                                                value={settings.log_level}
                                                onChange={handleChange}
                                            >
                                                <option value="DEBUG">Debug</option>
                                                <option value="INFO">Info</option>
                                                <option value="WARNING">Warning</option>
                                                <option value="ERROR">Error</option>
                                                <option value="CRITICAL">Critical</option>
                                            </Form.Select>
                                        </Form.Group>
                                    </Col>
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Fréquence de sauvegarde *</Form.Label>
                                            <Form.Select
                                                name="backup_frequency"
                                                value={settings.backup_frequency}
                                                onChange={handleChange}
                                            >
                                                <option value="hourly">Toutes les heures</option>
                                                <option value="daily">Quotidienne</option>
                                                <option value="weekly">Hebdomadaire</option>
                                                <option value="monthly">Mensuelle</option>
                                            </Form.Select>
                                        </Form.Group>
                                    </Col>
                                </Row>

                                <Form.Check
                                    type="checkbox"
                                    label="Mode maintenance"
                                    name="maintenance_mode"
                                    checked={settings.maintenance_mode}
                                    onChange={handleChange}
                                    className="mb-3"
                                />

                                <Form.Check
                                    type="checkbox"
                                    label="Mode debug"
                                    name="debug_mode"
                                    checked={settings.debug_mode}
                                    onChange={handleChange}
                                />
                            </Card.Body>
                        </Card>
                    </Tab>
                </Tabs>

                <div className="d-flex justify-content-end">
                    <Button
                        type="submit"
                        variant="primary"
                        size="lg"
                        disabled={saving}
                    >
                        <FaSave className="me-2" />
                        {saving ? 'Sauvegarde...' : 'Sauvegarder les paramètres'}
                    </Button>
                </div>
            </Form>
        </Container>
    );
};

export default SuperAdminSettings;
